import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowRight, BadgeCheck, CircleDollarSign, Handshake, LineChart, Send, Sparkles, Users } from 'lucide-react';
import styles from './Partners.module.scss';
import PartnerApplicationForm from './PartnerApplicationForm';

const heroImage = '/assets/images/luxury_partners_hero.jpg';
const detailImage = '/assets/images/panama_creator_professional_workspace_1778199372293.png';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.75, ease: [0.23, 1, 0.32, 1] } }
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.11 } }
};

const icons = [Handshake, Users, LineChart];
const processIcons = [Send, BadgeCheck, Sparkles, CircleDollarSign];

const Partners = () => {
  const { t } = useTranslation('partners');

  useEffect(() => {
    document.title = t('meta.title');
    window.scrollTo(0, 0);
  }, [t]);

  const benefits = t('benefits.items', { returnObjects: true });
  const process = t('process.steps', { returnObjects: true });
  const ideal = t('ideal.items', { returnObjects: true });
  const scrollToProcess = (event) => {
    event.preventDefault();
    document.getElementById('partner-process')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const scrollToApplication = (event) => {
    event.preventDefault();
    document.getElementById('partner-application')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className={styles.partnersPage}>
      <section className={styles.heroSection}>
        <div className={styles.heroMedia} aria-hidden="true">
          <img src={heroImage} alt="" />
          <div className={styles.heroOverlay} />
        </div>
        <div className={styles.container}>
          <motion.div className={styles.heroContent} initial="hidden" animate="visible" variants={stagger}>
            <motion.div className={styles.eyebrow} variants={fadeUp}>{t('hero.eyebrow')}</motion.div>
            <motion.h1 className={styles.heroTitle} variants={fadeUp}>{t('hero.title')}</motion.h1>
            <motion.p className={styles.heroSubtitle} variants={fadeUp}>{t('hero.subtitle')}</motion.p>
            <motion.div className={styles.heroActions} variants={fadeUp}>
              <a href="#partner-application" onClick={scrollToApplication} className={styles.primaryBtn}>
                {t('hero.primary')} <ArrowRight size={18} />
              </a>
              <a href="#partner-process" onClick={scrollToProcess} className={styles.secondaryBtn}>{t('hero.secondary')}</a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className={styles.introSection}>
        <div className={styles.container}>
          <div className={styles.introGrid}>
            <div>
              <div className={styles.sectionEyebrow}>{t('intro.eyebrow')}</div>
              <h2 className={styles.sectionTitle}>{t('intro.title')}</h2>
            </div>
            <p className={styles.sectionLead}>{t('intro.body')}</p>
          </div>
        </div>
      </section>

      <section className={styles.benefitsSection}>
        <div className={styles.container}>
          <div className={styles.benefitsGrid}>
            {benefits.map((item, index) => {
              const Icon = icons[index] || Sparkles;
              return (
                <motion.article
                  key={item.title}
                  className={styles.benefitCard}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.65, delay: index * 0.08, ease: [0.23, 1, 0.32, 1] }}
                >
                  <div className={styles.cardIcon}><Icon size={22} /></div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </motion.article>
              );
            })}
          </div>
        </div>
      </section>

      <section id="partner-process" className={styles.processSection}>
        <div className={styles.container}>
          <div className={styles.processHeader}>
            <div className={styles.sectionEyebrow}>{t('process.eyebrow')}</div>
            <h2 className={styles.sectionTitle}>{t('process.title')}</h2>
            <p className={styles.processLead}>{t('process.subtitle')}</p>
          </div>
          <div className={styles.processGrid}>
            {process.map((step, index) => {
              const Icon = processIcons[index] || BadgeCheck;
              return (
                <div key={step.title} className={styles.processStep}>
                  <div className={styles.stepMeta}>
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <Icon size={20} />
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className={styles.profileSection}>
        <div className={styles.container}>
          <div className={styles.profileGrid}>
            <div className={styles.profileImage}>
              <img src={detailImage} alt={t('ideal.image_alt')} />
            </div>
            <div className={styles.profileContent}>
              <div className={styles.sectionEyebrow}>{t('ideal.eyebrow')}</div>
              <h2 className={styles.sectionTitle}>{t('ideal.title')}</h2>
              <ul className={styles.idealList}>
                {ideal.map((item) => (
                  <li key={item}>
                    <BadgeCheck size={18} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <PartnerApplicationForm />

      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaBox}>
            <div className={styles.ctaCopy}>
              <div className={styles.sectionEyebrow}>{t('cta.eyebrow')}</div>
              <h2>{t('cta.title')}</h2>
              <p>{t('cta.text')}</p>
            </div>
            <div className={styles.ctaActions}>
              <a href="#partner-application" onClick={scrollToApplication} className={styles.darkBtn}>
                {t('cta.primary')} <ArrowRight size={18} />
              </a>
              <Link to="/contact" className={styles.lightBtn}>{t('cta.secondary')}</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partners;
